// How the AE's accounts split and sort (GRO-360, four sections since 20 Oct).
//
// Four sections, top to bottom, and each account lands in the first that applies:
//   1. Open opportunities: any open opp, furthest stage first (4 before 3 before 2 before 1, an
//      unnumbered stage last), then the lead deal's earliest close date (past dates first).
//   2. Meeting booked: a meeting still to come and no open opp, ordered by meeting date.
//   3. In conversation with the SDR: a prospect replied to the SDR's outreach (`responses`, the
//      same signal as the summary line's "replied to the SDR"), most recent reply first.
//   4. Everything else, by usage then name.
//
// Pure and imported by `node --test`.

import type { BookAccount, Opportunity } from '../api/types.ts';
import { openOpportunities } from '../sidebar/panelFacts.ts';

export const GROUP_OPPORTUNITY = 0;
export const GROUP_MEETING = 1;
export const GROUP_CONVERSATION = 2;
export const GROUP_REST = 3;

export const AE_GROUP_LABELS: Record<number, string> = {
  [GROUP_OPPORTUNITY]: 'Open opportunities',
  [GROUP_MEETING]: 'Meeting booked',
  [GROUP_CONVERSATION]: 'In conversation with the SDR',
  [GROUP_REST]: 'Everything else you own',
};

export interface AeSections {
  opportunities: BookAccount[];
  meetings: BookAccount[];
  conversations: BookAccount[];
  rest: BookAccount[];
}

/** Earlier of two YYYY-MM-DD days, ignoring a missing one. ISO days compare as strings. */
function earlierDay(first: string | null | undefined, second: string | null | undefined): string | null {
  if (first && second) return first <= second ? first : second;
  return first || second || null;
}

/** The next meeting on the account that has not happened: the strategist's or the AE's, whichever is first. */
export function nextMeetingDay(row: BookAccount, today: string): string | null {
  const upcoming = [row.next_meeting, row.ae_next_meeting].filter(
    (day): day is string => Boolean(day) && (day as string) >= today,
  );
  return upcoming.reduce<string | null>((earliest, day) => earlierDay(earliest, day), null);
}

/** The earliest close date among the open opportunities. Past dates stay, so an overdue deal leads. */
export function nextCloseDay(row: BookAccount): string | null {
  return openOpportunities(row).reduce<string | null>(
    (earliest, deal) => earlierDay(earliest, deal.close_date), null);
}

/** True when a prospect replied to the SDR's outreach: the summary line's own signal. */
export function hasSdrReply(row: BookAccount): boolean {
  return row.responses > 0;
}

/** The section an account belongs in: the first of the four that applies. */
export function aeGroup(row: BookAccount, today: string): number {
  if (openOpportunities(row).length > 0) return GROUP_OPPORTUNITY;
  if (nextMeetingDay(row, today) !== null) return GROUP_MEETING;
  if (hasSdrReply(row)) return GROUP_CONVERSATION;
  return GROUP_REST;
}

/** The deal the row leads with: the furthest-along open one (`1.` to `5.` in the stage name). */
export function leadDeal(row: BookAccount): Opportunity | null {
  const open = openOpportunities(row);
  if (!open.length) return null;
  return [...open].sort((first, second) => stageRank(second) - stageRank(first))[0] ?? null;
}

/** A deal's stage by its leading number ("3. Proposal" is 3); a stage with no number is 0, the lowest. */
export function stageRank(deal: Opportunity): number {
  return Number.parseInt(deal.stage ?? '', 10) || 0;
}

/** True for any stage 1 ("1. Qualification", "1. Discovery", ...), which the table and the panel strip draw in the warn colour. */
export function isStageOne(stage: string | null | undefined): boolean {
  return Number.parseInt((stage ?? '').trim(), 10) === 1;
}

// A row with no date goes last, then name.
function compareDays(first: string | null, second: string | null, direction: 1 | -1): number {
  if (first === second) return 0;
  if (!first) return 1;
  if (!second) return -1;
  return first < second ? -direction : direction;
}

// Furthest-along lead deal first, then its close date (earliest first), then name.
function byStageThenCloseDayThenName(first: BookAccount, second: BookAccount): number {
  const firstDeal = leadDeal(first);
  const secondDeal = leadDeal(second);
  const firstRank = firstDeal ? stageRank(firstDeal) : 0;
  const secondRank = secondDeal ? stageRank(secondDeal) : 0;
  if (firstRank !== secondRank) return secondRank - firstRank;
  return compareDays(firstDeal?.close_date ?? null, secondDeal?.close_date ?? null, 1)
    || first.account.localeCompare(second.account);
}

function byMeetingDayThenName(today: string) {
  return (first: BookAccount, second: BookAccount): number =>
    compareDays(nextMeetingDay(first, today), nextMeetingDay(second, today), 1)
    || first.account.localeCompare(second.account);
}

// Most recent reply first.
function byReplyDayThenName(first: BookAccount, second: BookAccount): number {
  return compareDays(first.last_reply_on?.slice(0, 10) ?? null, second.last_reply_on?.slice(0, 10) ?? null, -1)
    || first.account.localeCompare(second.account);
}

function byCreditsThenName(first: BookAccount, second: BookAccount): number {
  const firstCredits = first.credits_30 ?? -1;
  const secondCredits = second.credits_30 ?? -1;
  if (firstCredits !== secondCredits) return secondCredits - firstCredits;
  return first.account.localeCompare(second.account);
}

/** The AE's accounts in the four sections, each in its own order. Never mutates `rows`. */
export function splitAeAccounts(rows: BookAccount[], today: string): AeSections {
  const inGroup = (group: number) => rows.filter(row => aeGroup(row, today) === group);
  return {
    opportunities: inGroup(GROUP_OPPORTUNITY).sort(byStageThenCloseDayThenName),
    meetings: inGroup(GROUP_MEETING).sort(byMeetingDayThenName(today)),
    conversations: inGroup(GROUP_CONVERSATION).sort(byReplyDayThenName),
    rest: inGroup(GROUP_REST).sort(byCreditsThenName),
  };
}
